import { X } from 'lucide-react'
import { useState } from 'react'

import { useCarriers, useCreateParcel, useProjects } from '../api/client'
import { de } from '../i18n/de'

/** The parcel a return is started from — enough to name it and prefill the form. */
export interface ReturnSource {
  id: number
  label: string
  description: string | null
  projectId: number | null
}

/**
 * Add a parcel by hand — or, with `returnOf`, start the return of an incoming one.
 *
 * A return needs the number from the return label; content and project are prefilled from
 * the original (the backend does the same when they are left empty), and the note is where
 * the reason goes ("falsche Größe, Gutschrift erwartet").
 */
export function AddParcelDialog({
  onClose,
  returnOf,
  onCreated,
}: {
  onClose: () => void
  returnOf?: ReturnSource
  onCreated?: (id: number) => void
}) {
  const { data: carriers } = useCarriers()
  const { data: projects } = useProjects()
  const createParcel = useCreateParcel()

  const [trackingNumber, setTrackingNumber] = useState('')
  const [carrier, setCarrier] = useState('')
  const [description, setDescription] = useState(returnOf?.description ?? '')
  const [recipient, setRecipient] = useState('')
  const [recipientAddress, setRecipientAddress] = useState('')
  const [projectId, setProjectId] = useState(returnOf?.projectId ? String(returnOf.projectId) : '')
  const [notes, setNotes] = useState('')
  const [isReturn, setIsReturn] = useState(Boolean(returnOf))

  function submit(event: React.FormEvent) {
    event.preventDefault()
    createParcel.mutate(
      {
        tracking_number: trackingNumber.trim(),
        carrier: carrier || null,
        description: description.trim() || null,
        recipient_name: recipient.trim() || null,
        recipient_address: recipientAddress.trim() || null,
        project_id: projectId ? Number(projectId) : null,
        notes: notes.trim() || null,
        is_return: isReturn,
        return_of_id: returnOf?.id ?? null,
      },
      {
        onSuccess: (parcel) => {
          onClose()
          onCreated?.(parcel.id)
        },
      },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">
              {returnOf ? de.add.returnTitle : de.add.title}
            </h2>
            {returnOf && (
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                {de.add.returnOf(returnOf.label)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={de.actions.close}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-3">
          <Field
            label={isReturn ? de.add.returnNumberLabel : de.add.numberLabel}
            hint={isReturn ? de.add.returnNumberHint : de.add.numberHint}
          >
            <input
              required
              autoFocus
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
              placeholder="00340434161094042557"
              className={inputClass + ' font-mono'}
            />
          </Field>

          <Field label={de.parcel.carrier}>
            <select
              value={carrier}
              onChange={(event) => setCarrier(event.target.value)}
              className={inputClass}
            >
              <option value="">{de.add.carrierAuto}</option>
              {carriers?.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={de.parcel.description}>
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className={inputClass}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={de.parcel.recipient}>
              <input
                value={recipient}
                onChange={(event) => setRecipient(event.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label={de.parcel.project}>
              <select
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
                className={inputClass}
              >
                <option value="">{de.parcel.noProject}</option>
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={de.parcel.recipientAddress}>
            <input
              value={recipientAddress}
              onChange={(event) => setRecipientAddress(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label={isReturn ? de.add.returnNotesLabel : de.parcel.notes}>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={2}
              placeholder={isReturn ? de.add.returnNotesPlaceholder : undefined}
              className={inputClass}
            />
          </Field>

          {/* Started from a parcel it is a return by definition; only a return added from
              scratch needs the switch. */}
          {!returnOf && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={isReturn}
                onChange={(event) => setIsReturn(event.target.checked)}
                className="mt-0.5 accent-indigo-500"
              />
              <span>
                {de.add.isReturn}
                <span className="block text-xs text-slate-400">{de.add.isReturnHint}</span>
              </span>
            </label>
          )}
        </div>

        {createParcel.isError && (
          <p className="mt-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
            {(createParcel.error as Error).message}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryButton}>
            {de.actions.cancel}
          </button>
          <button
            type="submit"
            disabled={!trackingNumber.trim() || createParcel.isPending}
            className={primaryButton}
          >
            {returnOf ? de.add.returnSubmit : de.add.submit}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  )
}

/** Field styling without a width, for controls that size themselves (filter selects). */
export const inputBase =
  'rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-800'

export const inputClass = `w-full ${inputBase}`

export const primaryButton =
  'rounded-lg bg-indigo-500 px-3 py-1.5 text-sm font-medium text-slate-950 transition hover:bg-indigo-400 disabled:opacity-50'

export const secondaryButton =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm transition hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'

/** For the one action that destroys something. Outlined, not filled: it is not the
    primary thing to do here, it just must not be mistaken for the buttons next to it. */
export const dangerButton =
  'rounded-lg border border-rose-300 px-3 py-1.5 text-sm text-rose-700 transition hover:bg-rose-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950'
